import React, { useState } from "react";
import GitHubIcon from "@mui/icons-material/GitHub";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import SwipeOutlinedIcon from "@mui/icons-material/SwipeOutlined";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import KeyboardIcon from "@mui/icons-material/Keyboard";

import {
  accentText,
  mutedText,
  filledButton,
  outlineButton,
  Phone,
  PHONE,
  TourBar,
  FeatureTiles,
  TourStep,
  InternalsGrid,
  ApkDownload,
  ApkNote,
  useActiveStep,
} from "../components/Walkthrough";

import titleArt from "../assets/graphics/LongShot_Card.webp";
import menuShot from "../assets/demo_screenshots/longshot/menu.webp";
import aimShot from "../assets/demo_screenshots/longshot/aim.webp";
import bankShot from "../assets/demo_screenshots/longshot/bank.webp";
import curveShot from "../assets/demo_screenshots/longshot/curve.webp";
import trickShot from "../assets/demo_screenshots/longshot/trick.webp";
import lateShot from "../assets/demo_screenshots/longshot/late.webp";
import nightShot from "../assets/demo_screenshots/longshot/night.webp";
import gameOverShot from "../assets/demo_screenshots/longshot/gameover.webp";
import shopShot from "../assets/demo_screenshots/longshot/shop.webp";
import pitchesShot from "../assets/demo_screenshots/longshot/pitches.webp";
import missionsShot from "../assets/demo_screenshots/longshot/missions.webp";
import gameplayVideo from "../assets/videos/LongShot_Gameplay.mp4";

const REPO = "ElAyachie/Long-Shot";
const GITHUB_URL = `https://github.com/${REPO}`;
const APK_FILE = "LongShot.apk";
const MIN_ANDROID = "6.0";
// The WebGL build lives in public/games/longshot and is only downloaded when someone presses play.
const GAME_URL = "/games/longshot/index.html";
const GAME_SIZE = "8 MB";

// Game screenshots have no light and dark versions, so every screen pins the one image.
const shot = (img) => ({ light: img, dark: img });
const screen = (img, caption, alt) => ({ shot: shot(img), theme: "light", caption, alt });

const steps = [
  {
    id: "aim",
    label: "Aim",
    title: "Pull back, let go",
    body: "Press anywhere and pull back like a slingshot. The further you pull, the harder the shot, and a dotted guide shows where it's going, including the first bounce off the boards. A quick tap still fires straight up, like the original game.",
    detail: "The ball slows down as it travels, so a soft shot crawls through the defenders and a long one can fall short.",
    screens: [screen(aimShot, "Aiming a bank shot", "Pulling back to aim: a dotted line runs from the ball, off the right-hand board and towards the goal, past two defenders")],
  },
  {
    id: "bank",
    label: "Bank shots",
    title: "Use the walls",
    body: "Advertising boards line both touchlines and the ball bounces off them. Every bounce before a goal is worth a point: a bank shot, a double bank or a triple bank. One board segment glows gold, and hitting it pays coins.",
    screens: [screen(bankShot, "Off the board", "The ball rebounding off the right-hand board with a trail behind it")],
  },
  {
    id: "curve",
    label: "Curve",
    title: "Bend it, and mind the wind",
    body: "Swipe sideways while the ball is in the air to curve it around a defender. A strongly curved goal is a Banana. From eight goals in, the wind picks up and pushes the ball sideways, so you have to aim into it.",
    screens: [screen(curveShot, "Curving on the Cyber pitch", "A neon ball curving to the left on the dark Cyber pitch")],
  },
  {
    id: "tricks",
    label: "Trick shots",
    title: "Combine them",
    body: "Grazing a defender earns a close call, a shot that hits the center of a moving goal is Top Bins, and pinball bumpers turn up later on. Land two or more in one shot for a Trick Shot, three for a Super Trick and four for Insane.",
    screens: [screen(trickShot, "Super trick", "A Super Trick banner worth eight points, with Bank, Bumper and Close listed underneath")],
  },
  {
    id: "harder",
    label: "Harder",
    title: "It keeps getting harder",
    body: "One defender becomes four, then a goalkeeper steps in, the goal starts to slide, defenders learn to stop and change pace, and lanes get doubled up. Quick shots build a streak, and five in a row puts you On Fire for double points.",
    screens: [
      screen(lateShot, "Thirty goals in", "Late in a match: four defenders, a goalkeeper, two bumpers, a coin and a slow-motion pickup, with wind and On Fire shown at the top"),
      screen(nightShot, "Night match", "The Night Match pitch with the goalkeeper in goal"),
    ],
  },
  {
    id: "progress",
    label: "Rewards",
    title: "Every match counts",
    body: "Each match pays out coins and XP. Levels come with rank titles, from Sunday League up to G.O.A.T., and unlock new items. A paid continue gets you back in once per match, with a shield.",
    screens: [screen(gameOverShot, "Full time", "The match summary: score 41, coins and XP earned, a level up, and progress on three missions")],
  },
  {
    id: "shop",
    label: "Shop",
    title: "Balls with perks",
    body: "Coins buy new balls and pitches. Every ball changes how it plays: Ice keeps its pace, Toxic loses no speed off the boards, Lava ignores the wind and Neon curves harder. Pitches range from a night match and a Game Boy-style retro look to futsal, beach, snow and neon Cyber.",
    screens: [
      screen(shopShot, "Balls", "The shop's ball tab with nine balls, their perks and prices"),
      screen(pitchesShot, "Pitches", "The shop's pitch tab with seven themes"),
    ],
  },
  {
    id: "goals",
    label: "Missions",
    title: "Something to come back for",
    body: "Three missions are always running, like scoring bank shots or landing trick shots, and they get harder as you complete them. A seven-day daily bonus builds up to a big reward, and your career stats are kept alongside.",
    screens: [
      screen(missionsShot, "Missions and daily bonus", "The missions screen: three missions with progress bars, the seven-day bonus calendar and career stats"),
      screen(menuShot, "Title screen", "The title screen showing level, rank, coins and best score"),
    ],
  },
];

const features = [
  { icon: TouchAppIcon, title: "Aim and power", text: "Pull back to aim, harder for more pace.", href: "#aim" },
  { icon: CallSplitIcon, title: "Bank shots", text: "Bounce it off the boards for points.", href: "#bank" },
  { icon: AutoAwesomeIcon, title: "Trick shots", text: "Curve, graze, ricochet, combine.", href: "#tricks" },
  { icon: EmojiEventsIcon, title: "Reasons to return", text: "Missions, levels, a daily bonus, a shop.", href: "#goals" },
];

export const tech = ["Unity 6", "C#", "WebGL", "2D physics", "uGUI", "Pixel art"];

const howToPlay = [
  { icon: TouchAppIcon, text: "Press and pull back to aim. Let go to shoot." },
  { icon: PlayArrowRoundedIcon, text: "Tap for a quick, straight shot." },
  { icon: SwipeOutlinedIcon, text: "Swipe sideways while the ball flies to curve it." },
  { icon: KeyboardIcon, text: "On a computer, use the mouse, or Space to shoot straight." },
];

const internals = [
  {
    title: "Rebuilt in 2026",
    text: "Long Shot began as a one-tap game I made in college: the ball went straight up, four defenders walked back and forth, and a hit reloaded the scene. The 2026 rebuild moves it to Unity 6 and keeps the original sprites and sounds, but rewrites every script around a proper game loop.",
  },
  {
    title: "Drawn in code",
    text: "Everything added in the rebuild is generated at runtime. The goalkeeper, pickups, bumpers and icons are pixel art drawn from text grids at the same pixel size as the original players. The pitch is painted per theme with its markings, the menus are built in code, and the coin, power-up and whistle sounds are synthesized.",
  },
  {
    title: "Shot physics",
    text: "A shot has a direction and a speed that drops at a constant rate. It loses some speed across the ball when it hits a board, swerves sideways when curved, and drifts with the wind. Movement is sub-stepped and checked against the defenders' outlines, so a ball that clearly missed never counts as a hit. The aim guide runs the same simulation.",
  },
  {
    title: "A difficulty curve",
    text: "Difficulty comes from one function of goals scored: how many defenders, how fast, whether they feint, when the keeper, wind, bumpers and moving goal arrive, and how many lanes are doubled up. Each new threat gets its own banner when it appears.",
  },
  {
    title: "Progress that persists",
    text: "Coins, XP, unlocks, missions, the daily streak and lifetime stats are saved as JSON. Missions scale with how many you've finished, and rewards are paid the moment one completes, mid-match.",
  },
  {
    title: "Playable in the browser",
    text: "The game on this page is a WebGL build with a custom loader. It's compressed so it runs on a plain static host with no server setup, and it only downloads when you press play. Mouse and touch both work, and your progress is saved in the browser.",
  },
];

const stepIds = steps.map((s) => s.id);

function PhoneShell({ children, size }) {
  return (
    <figure className={`shrink-0 ${size}`}>
      <div className="rounded-[1.6rem] lg:rounded-[2rem] bg-[#16171b] p-[5px] lg:p-[7px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)] ring-1 ring-black/10 dark:ring-white/15">
        <div className="relative w-full overflow-hidden rounded-[1.3rem] lg:rounded-[1.6rem] bg-[#3e7d2e]" style={{ aspectRatio: `${PHONE.width} / ${PHONE.height}` }}>
          {children}
        </div>
      </div>
    </figure>
  );
}

// Looping, muted gameplay clip in a phone frame.
function GameplayClip({ size }) {
  return (
    <PhoneShell size={size}>
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={gameplayVideo}
        poster={lateShot}
        autoPlay
        muted
        loop
        playsInline
        aria-label="Gameplay: aimed shots bending past defenders and banking off the boards into the goal"
      />
    </PhoneShell>
  );
}

// The real game, loaded into the phone frame only when the visitor asks for it.
function GamePlayer() {
  const [playing, setPlaying] = useState(false);
  return (
    <PhoneShell size="w-[min(86vw,360px)]">
      {playing ? (
        <iframe
          src={GAME_URL}
          title="Long Shot, playable in the browser"
          className="absolute inset-0 w-full h-full border-0"
          allow="autoplay; fullscreen"
        />
      ) : (
        <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" aria-label={`Play Long Shot here (${GAME_SIZE} download)`}>
          <img src={menuShot} alt="" className="absolute inset-0 w-full h-full object-cover blur-[2px] scale-105" />
          <span className="absolute inset-0 bg-black/45 group-hover:bg-black/30 transition-colors flex flex-col items-center justify-center gap-3 text-white">
            <span className="w-20 h-20 rounded-full grid place-items-center bg-[#eb4d5c] dark:bg-[#c9a7ff] dark:text-[#0f193d] shadow-lg group-hover:scale-105 transition-transform">
              <PlayArrowRoundedIcon sx={{ fontSize: 56 }} aria-hidden="true" />
            </span>
            <span className="text-2xl font-bold drop-shadow">Play Long Shot</span>
            <span className="text-sm font-medium opacity-90">{GAME_SIZE} download</span>
          </span>
        </button>
      )}
    </PhoneShell>
  );
}

function LongShotProject() {
  const active = useActiveStep(stepIds);

  return (
    <div className="w-full text-[black] dark:text-[white] pb-16">
      {/* Hero: text, gameplay clip and a screenshot */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-8 lg:pt-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
          <img src={titleArt} alt="" aria-hidden="true" width="1200" height="785" className="h-20 lg:h-24 w-auto rounded-2xl shadow-md" />
          <p className={`pt-4 text-sm font-semibold tracking-widest uppercase ${accentText}`}>Unity game · Play it in your browser</p>
          <h1 className="text-[42px] sm:text-5xl lg:text-[56px] leading-tight font-bold pb-3">Long Shot</h1>
          <p className="text-xl lg:text-[22px] leading-relaxed text-[#333] dark:text-[#d6d9e3]">
            Score from your own half. Aim past a line of defenders, bank shots off the boards, bend them
            around a goalkeeper, and see how far you get before someone gets a foot to it.
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-6">
            <a href="#play" className={filledButton}>
              <PlayArrowRoundedIcon fontSize="small" aria-hidden="true" />
              Play now
            </a>
            <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={outlineButton}>
              <GitHubIcon fontSize="small" aria-hidden="true" />
              Source code
            </a>
            <ApkDownload repo={REPO} file={APK_FILE} />
          </div>
          <ApkNote repo={REPO} file={APK_FILE} minAndroid={MIN_ANDROID} className="pt-3" />
          <ul className="flex flex-wrap justify-center lg:justify-start gap-2 pt-6" aria-label="Built with">
            {tech.map((t) => (
              <li key={t} className="rounded-full border border-[#eb4d5c] dark:border-[#c9a7ff] px-3 py-0.5 text-sm font-medium text-[#eb4d5c] dark:text-[#c9a7ff]">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-start justify-center gap-4 lg:gap-6">
          <GameplayClip size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          <div className="pt-10 lg:pt-14">
            <Phone screen={screen(trickShot, null, "A Super Trick landing late in a match")} size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          </div>
        </div>
      </section>

      {/* What's in it, at a glance. Each tile jumps to its part of the tour. */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-14">
        <FeatureTiles features={features} />
      </section>

      {/* Play it here */}
      <section id="play" className="scroll-mt-6 w-full max-w-5xl mx-auto px-4 pt-16 flex flex-col lg:flex-row items-center gap-10 lg:gap-16" aria-labelledby="play-heading">
        <GamePlayer />
        <div className="flex-1 text-center lg:text-left">
          <h2 id="play-heading" className="text-4xl lg:text-5xl font-bold pb-2">Play it here</h2>
          <p className="text-lg lg:text-xl pb-6">
            The full game runs right on this page, on a phone or a computer. Nothing to install.
          </p>
          <ul className="flex flex-col gap-3 items-center lg:items-start">
            {howToPlay.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-left">
                <span className="shrink-0 w-10 h-10 rounded-full grid place-items-center bg-[#fde3e5] text-[#c43646] dark:bg-[#c9a7ff]/20 dark:text-[#c9a7ff]">
                  <Icon fontSize="small" aria-hidden="true" />
                </span>
                <span className="text-lg">{text}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-7">
            <a href={GAME_URL} target="_blank" rel="noreferrer" className={outlineButton}>
              <OpenInNewIcon fontSize="small" aria-hidden="true" />
              Open full screen
            </a>
          </div>
          <p className={`pt-4 text-sm ${mutedText}`}>Your coins, unlocks and best score are saved in this browser.</p>
        </div>
      </section>

      {/* The tour */}
      <section className="w-full pt-16" aria-labelledby="tour-heading">
        <div className="w-full max-w-5xl mx-auto px-4">
          <h2 id="tour-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">How it plays</h2>
          <p className="text-lg lg:text-xl text-center lg:text-left pb-6">
            From your first shot to the late game, and what keeps you coming back.
          </p>
        </div>

        <TourBar steps={steps} active={active} />

        <ol className="w-full max-w-5xl mx-auto px-4">
          {steps.map((step, i) => (
            <TourStep key={step.id} step={step} index={i} total={steps.length} />
          ))}
        </ol>
      </section>

      {/* Engineering notes */}
      <section id="built" className="scroll-mt-6 w-full max-w-5xl mx-auto px-4 pt-10" aria-labelledby="built-heading">
        <h2 id="built-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">How it's built</h2>
        <p className="text-lg lg:text-xl text-center lg:text-left pb-7">
          A small college game, rebuilt with physics, progression and polish.
        </p>
        <InternalsGrid items={internals} />
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={filledButton}>
            <GitHubIcon fontSize="small" aria-hidden="true" />
            Read the code
          </a>
          <ApkDownload repo={REPO} file={APK_FILE} />
        </div>
        <p className={`pt-8 text-center ${mutedText}`}>Created by Mohamed Oulal in college. Rebuilt in 2026.</p>
      </section>
    </div>
  );
}

export default LongShotProject;
